import React, { useEffect } from 'react';

import { makeStyles, useTheme } from "@material-ui/core/styles";

import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import Slide from '@material-ui/core/Slide';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Tooltip from '@material-ui/core/Tooltip';


const useStyles = makeStyles(theme => ({
    compact: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    grid: {
        borderBottomColor: theme.palette.divider,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        '&:last-child': {
            borderBottom: 'none'
        },
        '&:hover': {
            backgroundColor: theme.palette.grey[100]
        }
    },
    bordered: {
        borderBottomColor: theme.palette.divider,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
    },
    close: {
        padding: theme.spacing(0.5),
    },
    snackbar: {
        backgroundColor: theme.palette.primary.main
    },
    dialogTitle: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
    },
    categoryTitle: {
        textTransform: 'uppercase',
        marginTop: theme.spacing(2)
    },
    categoryButton: {
        marginRight: theme.spacing(1)
    },
    backToTop: {
        position: 'absolute',
        left: 15,
        color: theme.palette.primary.main
    },
    suggestionIcon: {
        '& .MuiSvgIcon-root': {
            fontSize: '2.2rem'
        }
    }
}));

const TransitionUp = (props) => {
    return <Slide {...props} direction="up" />;
}

const DialogTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const SuggestionItem = (props) => {
    const {
        triggerAlert
    } = props;

    const [added, setAdded] = React.useState(props.added);
    const classes = useStyles();

    return (
        <div className={classes.suggestionIcon}>
            {!added &&
                <IconButton
                    onClick={() => {
                        triggerAlert();
                        setAdded(true);
                    }}
                    color='primary'
                >
                    <AddCircleIcon />
                </IconButton>
            }
            {added &&
                <Tooltip title="Added to ceremony">
                    <span>
                        <IconButton
                            disabled
                        >
                            <CheckCircleIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            }
        </div>
    )
}

const SuggestionsDialog = (props) => {
    const {
        open,
        scroll,
        handleClose,
        title,
        weddingId,
        sectionId,
        sectionInstruction,
        sectionText,
        brideFirstName,
        groomFirstName,
        officiantFirstName,
        setHtml
    } = props;

    const [suggestions, setSuggestions] = React.useState([]);
    const [categories, setCategories] = React.useState([]);

    const [loading, setLoading] = React.useState(true);

    const classes = useStyles();

    const descriptionElementRef = React.useRef(null);
    useEffect(() => {
        const { current: descriptionElement } = descriptionElementRef;
        if (descriptionElement !== null) {
            descriptionElement.focus();
        }
        if (open === true) {
            new Promise((resolve, reject) => {
                let url = `/api/ceremony/templates/sections/${sectionId}`
                fetch(url)
                    .then(response => {
                        return response.json();
                    })
                    .then(result => {
                        setSuggestions(result);
                        const categories = [];
                        let pastCategory = '';
                        for (const item of result.filter(a => a.category_name)) {
                            const itemName = item.category_name.trim();
                            if (itemName !== pastCategory && categories.filter(row => row.name === itemName) < 1) {
                                categories.push({ id: item.item_category_id, name: itemName })
                            }
                            pastCategory = itemName;
                        }
                        setCategories(categories);
                        setLoading(false);
                    })
            })
        }
    }, [open, sectionId]);

    const newlines = text => (
        text.replace(/\n/g, '<br/>').replace(/\r/g, '')
    );

    const textClean = text => (
        text.replace(/&.{1,6}\;/gi, '').replace(/[\"\']/g, '').trim()
    );

    const piping = text => (
        text.replace(/\[BRIDE\]/g, brideFirstName)
            .replace(/\[GROOM\]/g, groomFirstName)
            .replace(/\[OFFICIANT\]/g, officiantFirstName || 'Officiant')
    );

    const [alertOpen, setAlertOpen] = React.useState(false);
    const triggerAlert = suggestionId => () => {
        const data = {
            _token: window._token,
        };
        new Promise((resolve, reject) => {
            let url = `/api/ceremony/${weddingId}/add-suggestion/${suggestionId}`;
            fetch(url, {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setAlertOpen(true);
                    setHtml(result.text);
                })
                .catch(reason => {
                    alert('An error prevented saving. Please refresh the page and try again.');
                })
        })
    };
    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertOpen(false);
    };

    /* TODO: organize by category
     * Potentially have navigation for categories, and even include a count of number of items per category? 
     * have it as pills?
     */

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    let categoryName;

    const handleCategoryButtonClick = id => () => {
        const elmnt = document.getElementById(`category-${id}`);
        if (elmnt !== null) {
            elmnt.scrollIntoView();
        }
    }

    const scrollToTop = () => {
        document.getElementById('suggestions-dialog-top').scrollIntoView();
    }

    return (
        <div>
            <Dialog
                open={open}
                onClose={handleClose}
                scroll={scroll}
                maxWidth='lg'
                aria-labelledby="scroll-dialog-title"
                aria-describedby="scroll-dialog-description"
                TransitionComponent={DialogTransition}
                fullScreen={fullScreen}
            >
                <DialogTitle
                    id="scroll-dialog-title"
                    className={classes.dialogTitle}
                >
                    {`Suggestions for: ${title}`}
                </DialogTitle>
                <DialogContent dividers={scroll === 'paper'}>
                    <DialogContentText
                        id="scroll-dialog-description"
                        ref={descriptionElementRef}
                        tabIndex={-1}
                        component="div"
                        className={classes.compact}
                    >
                        <Grid
                            container
                            spacing={3}
                            direction="row"
                            justify="center"
                            alignItems="center"
                            id='suggestions-dialog-top'
                        >
                            {sectionInstruction && 
                                <Grid item xs={12} className={classes.bordered}>
                                    <Typography
                                        color='textPrimary'
                                        dangerouslySetInnerHTML={
                                            {__html: newlines(piping(sectionInstruction))}
                                        }
                                    />
                                </Grid>
                            }
                            {!loading && categories.length > 0 &&
                                <Grid item xs={12} className={classes.bordered}>
                                    <Box align="center">
                                        {categories.map(item => (
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                key={`category-button-${item.id}`}
                                                className={classes.categoryButton}
                                                onClick={handleCategoryButtonClick(item.id)}
                                            >
                                                {item.name}
                                            </Button>
                                        ))}
                                    </Box>
                                </Grid>
                            }
                            {!loading && suggestions && suggestions.map((item) => {
                                const searchText = piping(item.item_text);
                                /* compare current section text with item text to see if the suggestion is already used */
                                const added = item.item_text && (
                                    sectionText.indexOf(
                                        searchText.substring(0, 200)
                                    ) >= 0
                                    || textClean(newlines(sectionText)).indexOf(
                                        textClean(newlines(searchText)).substring(0, 200)
                                    ) >= 0
                                );
                                const showCategory = item.category_name && item.category_name.trim() !== categoryName;
                                if (showCategory) {
                                    categoryName = item.category_name.trim();
                                }
                                return (
                                    <React.Fragment key={item.item_id}>
                                        {showCategory && 
                                            <Grid item xs={12} className={classes.bordered} id={`category-${item.item_category_id}`}>
                                                <Typography
                                                    variant="h6"
                                                    color="secondary"
                                                    className={classes.categoryTitle}
                                                >
                                                    {categoryName}
                                                </Typography>
                                            </Grid>
                                        }
                                        <Grid
                                            item
                                            xs={12}
                                            className={classes.grid}
                                        >
                                            <Box display="flex" alignItems="center">
                                                <Box flexGrow={1}>
                                                    <Typography
                                                        variant="h6"
                                                        color="textPrimary"
                                                        dangerouslySetInnerHTML={
                                                            {__html: piping(item.item_name)}
                                                        }
                                                    />
                                                    <Typography
                                                        dangerouslySetInnerHTML={
                                                            {__html: newlines(piping(item.item_text))}
                                                        }
                                                    />
                                                </Box>
                                                <Box>
                                                    <SuggestionItem
                                                        added={added}
                                                        triggerAlert={triggerAlert(item.item_id)}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </React.Fragment>
                                )
                            })}
                            {!loading && suggestions.length == 0 &&
                                <Box mt={1}>
                                    <Typography>None</Typography>
                                </Box>
                            }
                            {loading && <CircularProgress />}
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={scrollToTop} className={classes.backToTop}>
                        Back to top
                    </Button>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={alertOpen}
                autoHideDuration={3000}
                onClose={handleAlertClose}
                ContentProps={{
                    'aria-describedby': 'message-id',
                    'className': classes.snackbar
                }}
                TransitionComponent={TransitionUp}
                message={<span id="message-id">Added text to ceremony</span>}
                action={[
                    <IconButton
                        key="close"
                        aria-label="close"
                        color="inherit"
                        className={classes.close}
                        onClick={handleAlertClose}
                    >
                        <CloseIcon />
                    </IconButton>,
                ]}
            />
        </div>
    );
}

export default SuggestionsDialog;
